/**
 * VisitorSuggestionsAdmin — moderation queue for crowdsourced business
 * suggestions submitted through the public "Suggest a business" form.
 *
 * Route: /admin/foodpulse/business-suggestions
 *
 * Flow: admin sees pending suggestions, can edit any field inline, then
 * either Publish (creates a `food_businesses` doc) or Reject.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Loader2, Check, X, ChevronDown, ChevronUp, Mail, Phone } from "lucide-react";
import { http } from "@/lib/api";

const GREEN  = "#0F3D2E";
const CREAM  = "#FBF7EF";
const AMBER  = "#E4A140";
const PURPLE = "#7C3AED";

const EDITABLE_FIELDS = [
  ["business_name",     "Business name"],
  ["business_address",  "Address"],
  ["city",              "City"],
  ["state",             "State"],
  ["business_category", "Category"],
  ["business_phone",    "Business phone"],
  ["business_email",    "Business email"],
  ["business_website",  "Website"],
  ["notes",             "Notes"],
];

function SuggestionRow({ item, onChange }) {
  const [open, setOpen]     = useState(false);
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState(null);
  const [form, setForm]     = useState({ ...item });

  const save = async () => {
    setBusy(true); setMsg(null);
    const patch = {};
    for (const [k] of EDITABLE_FIELDS) {
      if (form[k] !== item[k]) patch[k] = form[k];
    }
    if (Object.keys(patch).length === 0) { setMsg({ ok: true, text: "No changes." }); setBusy(false); return; }
    try {
      await http.patch(`/foodpulse/business/suggestions/${item.id}`, patch);
      setMsg({ ok: true, text: "Saved." });
      onChange();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || "Save failed" }); }
    setBusy(false);
  };

  const publish = async () => {
    if (!window.confirm(`Publish "${form.business_name}" to the FoodPulse directory?`)) return;
    setBusy(true); setMsg(null);
    try {
      const r = await http.post(`/foodpulse/business/suggestions/${item.id}/publish`);
      setMsg({ ok: true, text: `Published as ${r.data.business.slug}` });
      onChange();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || "Publish failed" }); }
    setBusy(false);
  };

  const reject = async () => {
    if (!window.confirm(`Reject "${form.business_name}"?`)) return;
    setBusy(true); setMsg(null);
    try {
      await http.post(`/foodpulse/business/suggestions/${item.id}/reject`);
      setMsg({ ok: true, text: "Rejected." });
      onChange();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || "Reject failed" }); }
    setBusy(false);
  };

  const statusColor = {
    pending:   "#FDE68A",
    published: "#BBF7D0",
    rejected:  "#FCA5A5",
  }[item.status] || "#E9D8B4";

  return (
    <div className="rounded-2xl border bg-white p-4 mb-3" style={{ borderColor: "#E9D8B4" }} data-testid={`suggestion-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: statusColor, color: GREEN }}>{item.status}</span>
            <div className="text-sm font-bold" style={{ color: GREEN }}>{item.business_name}</div>
          </div>
          <div className="text-xs text-gray-600">{item.business_address}</div>
          <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
            <span>By <b>{item.suggester_name}</b></span>
            <a href={`mailto:${item.suggester_email}`} className="inline-flex items-center gap-0.5 hover:underline"><Mail size={10} /> {item.suggester_email}</a>
            {item.suggester_phone && <a href={`tel:${item.suggester_phone}`} className="inline-flex items-center gap-0.5 hover:underline"><Phone size={10} /> {item.suggester_phone}</a>}
            <span className="text-gray-400">· {item.created_at?.slice(0, 10)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen(!open)} className="rounded-full border px-3 py-1 text-xs font-semibold inline-flex items-center gap-1" style={{ borderColor: GREEN, color: GREEN }} data-testid={`toggle-${item.id}`}>
            {open ? <><ChevronUp size={12} /> Hide</> : <><ChevronDown size={12} /> Review</>}
          </button>
          {item.status === "pending" && (
            <>
              <button onClick={publish} disabled={busy} className="rounded-full px-3 py-1 text-xs font-bold inline-flex items-center gap-1" style={{ background: GREEN, color: CREAM }} data-testid={`publish-${item.id}`}>
                <Check size={12} /> Publish
              </button>
              <button onClick={reject} disabled={busy} className="rounded-full px-3 py-1 text-xs font-bold inline-flex items-center gap-1" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid={`reject-${item.id}`}>
                <X size={12} /> Reject
              </button>
            </>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t grid gap-2" style={{ borderColor: "#E9D8B4" }} data-testid={`edit-${item.id}`}>
          {EDITABLE_FIELDS.map(([k, label]) => (
            <label key={k} className="text-xs">
              <span className="block text-gray-500 mb-0.5">{label}</span>
              {k === "notes" || k === "business_address" ? (
                <textarea
                  rows={k === "notes" ? 3 : 2}
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="w-full border rounded-lg px-2 py-1 text-sm"
                  style={{ borderColor: "#E9D8B4" }}
                />
              ) : (
                <input
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="w-full border rounded-lg px-2 py-1 text-sm"
                  style={{ borderColor: "#E9D8B4" }}
                />
              )}
            </label>
          ))}
          <div className="flex items-center gap-2 mt-2">
            <button onClick={save} disabled={busy} className="rounded-full px-4 py-2 text-xs font-bold" style={{ background: PURPLE, color: "white" }} data-testid={`save-${item.id}`}>
              {busy ? <Loader2 className="animate-spin inline" size={12} /> : "Save edits"}
            </button>
            {msg && <span className={`text-xs ${msg.ok ? "text-green-700" : "text-red-700"}`}>{msg.text}</span>}
          </div>
          {item.status === "published" && item.resulting_business_slug && (
            <Link to={`/food-pulse/businesses/${item.resulting_business_slug}`} className="text-xs mt-2 hover:underline" style={{ color: AMBER }} target="_blank">
              → View published listing
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default function VisitorSuggestionsAdmin() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const reload = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await http.get("/foodpulse/business/suggestions", { params: { status, limit: 200 } });
      setItems(r.data.items || []);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [status]);

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="visitor-suggestions-admin">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-semibold mb-2" style={{ color: GREEN }}>
          <ArrowLeft size={12} /> Admin home
        </Link>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-black" style={{ color: GREEN }}>Visitor Business Suggestions</h1>
            <p className="text-sm text-gray-600">Community-crowdsourced entries. Edit, then Publish or Reject.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-full px-3 py-2 text-sm" data-testid="status-filter">
              <option value="pending">Pending</option>
              <option value="published">Published</option>
              <option value="rejected">Rejected</option>
              <option value="all">All</option>
            </select>
            <button onClick={reload} className="rounded-full border px-3 py-2 text-sm" style={{ borderColor: GREEN, color: GREEN }} data-testid="reload-btn">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {err && <div className="rounded-xl border p-3 mb-4 text-sm text-red-700" style={{ borderColor: "#F0C0C0", background: "#FEE7DA" }}>{err}</div>}

        {loading ? (
          <div className="p-10 text-center"><Loader2 className="mx-auto animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border p-10 text-center bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="empty-state">
            <div className="text-gray-600">No {status} suggestions.</div>
          </div>
        ) : (
          <div data-testid="suggestions-list">
            <div className="text-xs text-gray-500 mb-2">Showing {items.length} · {status}</div>
            {items.map((it) => <SuggestionRow key={it.id} item={it} onChange={reload} />)}
          </div>
        )}
      </div>
    </main>
  );
}
